import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { ArrowUp, MessageCircle, X } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import TID from "@/lib/testIds";

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      data-testid={TID.scrollProgress}
      style={{ scaleX: scrollYProgress }}
      className="fixed top-0 left-0 right-0 h-1 origin-left z-[60] bg-gradient-to-r from-brand-blue to-brand-purple"
    />
  );
};

export const FloatingWhatsApp = () => {
  return (
    <a
      href={buildWhatsAppLink()}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={TID.floatWhatsApp}
      aria-label="Chat on WhatsApp"
      className="fixed bottom-6 right-6 z-[55] inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500 text-white shadow-elevated hover:bg-emerald-600 transition-transform hover:scale-105 animate-pulse-soft"
    >
      <MessageCircle className="w-7 h-7" />
    </a>
  );
};

export const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          data-testid={TID.floatBackToTop}
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          className="fixed bottom-6 right-24 z-[55] w-12 h-12 rounded-full bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-white/10 shadow-elevated inline-flex items-center justify-center hover:-translate-y-1 transition"
        >
          <ArrowUp className="w-5 h-5 text-slate-900 dark:text-white" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const stored = window.localStorage.getItem("nds-cookies");
    if (!stored) setOpen(true);
  }, []);
  const close = (v) => {
    window.localStorage.setItem("nds-cookies", v);
    setOpen(false);
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={TID.cookieBanner}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] rounded-2xl glass shadow-elevated p-4 md:p-5"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="font-display font-semibold text-slate-900 dark:text-white">
                We value your privacy
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                We use cookies to enhance your experience and analyze traffic.
              </p>
            </div>
            <button
              aria-label="Close"
              onClick={() => close("declined")}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              data-testid={TID.cookieAccept}
              onClick={() => close("accepted")}
              className="h-9 px-4 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple text-white text-sm font-semibold"
            >
              Accept all
            </button>
            <button
              data-testid={TID.cookieDecline}
              onClick={() => close("declined")}
              className="h-9 px-4 rounded-full border border-slate-200 dark:border-white/10 text-sm font-semibold text-slate-700 dark:text-slate-200"
            >
              Decline
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
